import { useState, useCallback, useMemo } from 'react';
import { Baseline } from '@/types';
import {
  Inline,
  InlineItem,
  Input,
  Placeholder,
  Stack,
  useKeyboardShortcut,
} from '@kinesis/bungle';
import { shortcuts } from '@/shortcuts';
import { isEmpty } from 'lodash/fp';
import Fuse from 'fuse.js';
import { Item } from './item';

const fuseOptions = {
  isCaseSensitive: false,
  shouldSort: true,
  includeMatches: false,
  findAllMatches: true,
  minMatchCharLength: 0,
  keys: ['sector', 'subSector', 'dataType', 'dataTypeSource'],
};

const BaselineList = (props: BaselineListProps) => {
  const { baseline: baselines, now } = props;
  const ref = useKeyboardShortcut({
    predicate: shortcuts.SEARCH_ITEMS.handler,
    callback: (event) => {
      event.preventDefault();
      ref.current?.focus();
    },
  });
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (isEmpty(search)) return baselines;
    const fuse = new Fuse(baselines, fuseOptions);
    const results = fuse.search(search);
    return results.map(({ item }) => item);
  }, [search, baselines]);
  const onCancelSearch = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setSearch('');
      } else if (event.key === 'Escape') {
        ref.current?.blur();
      }
    },
    [ref],
  );

  return (
    <Stack space='medium'>
      <Inline>
        <InlineItem sizing='fill-container'>
          <Input
            ref={ref as any}
            type='search'
            placeholder={`Search (${shortcuts.SEARCH_ITEMS.mnemonic})`}
            onChange={setSearch}
            onKeyDown={onCancelSearch as any}
            value={search}
            magnitude='large'
            autoFocus
          />
        </InlineItem>
      </Inline>
      {filtered.map((baseline) => (
        <Item key={baseline.dataTypeSource} now={now} baseline={baseline} />
      ))}
      {isEmpty(search) && isEmpty(filtered) && (
        <Placeholder>There are no baseline elements.</Placeholder>
      )}
      {!isEmpty(search) && isEmpty(filtered) && (
        <Placeholder>There are no matching baseline elements.</Placeholder>
      )}
    </Stack>
  );
};

export type BaselineListProps = {
  now: string;
  baseline: Baseline[];
};

export { BaselineList };
